<script setup lang="ts">
// Custom renderer for ```mermaid blocks. Registered GLOBALLY via
// setCustomComponents({ mermaid: HqMermaid }) so it REPLACES markstream's built-in
// MermaidBlockNode for every diagram.
//
// Why we own this: markstream's built-in mermaid node re-creates itself shortly after
// mount (its own reconciliation), and under a real cold-load (SSE open + journal +
// artifacts + scroll all firing) its render-dedup cache orphans the in-flight async
// render - the re-created node awaits a dead promise and stays stuck "pending" forever
// (the diagram "renders then disappears"). In an isolated mount there's no contention,
// so it recovers - which is why it only broke in the live app. See the project memory
// "mermaid renders then disappears".
//
// Owning the render makes it robust: we render with our own mermaid instance and a
// MODULE-LEVEL cache keyed by source+theme, so even if markstream re-creates THIS
// component, onMounted re-injects the cached SVG instantly instead of awaiting any
// shared/aborted promise. The diagram source arrives as `node.code` (verified against
// markstream-vue@1.0.1: the override receives the code_block node).
import { ref, shallowRef, watch, onMounted } from 'vue';
import { getMermaid } from '../lib/mermaidFallback';
import { autoFixMermaid } from '../lib/mermaidAutoFix';
import { useI18n } from '../lib/i18n';

interface MermaidNode { code?: string; raw?: string; content?: string }
const props = defineProps<{ node?: MermaidNode; isDark?: boolean }>();
const { t } = useI18n();

const svg = shallowRef('');
const failed = ref(false);
// Module-level so it survives a component re-create: a re-mount re-injects instantly.
const cache = new Map<string, string>();
let seq = 0;

function source(): string {
  const n = props.node ?? {};
  return (n.code ?? n.content ?? n.raw ?? '').toString();
}

async function render(): Promise<void> {
  const src = source().trim();
  if (!src) { svg.value = ''; failed.value = false; return; }
  const dark = props.isDark ?? (typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark');
  const key = `${dark ? 'd' : 'l'}|${src}`;

  const hit = cache.get(key);
  if (hit) { svg.value = hit; failed.value = false; return; }

  const mermaid = await getMermaid();
  if (!mermaid) { failed.value = true; svg.value = ''; return; }
  const mine = ++seq;
  try {
    mermaid.initialize({ startOnLoad: false, theme: dark ? 'dark' : 'default', securityLevel: 'strict' });
    let out: { svg: string };
    try {
      out = await mermaid.render(`hq-mmd-${mine}`, src);
    } catch {
      // One best-effort repair pass (quote bare labels etc.), same as the fallback.
      const fixed = autoFixMermaid(src);
      if (fixed === src) throw new Error('mermaid parse failed');
      out = await mermaid.render(`hq-mmd-${mine}r`, fixed);
    }
    if (mine !== seq) return; // a newer render superseded this one
    cache.set(key, out.svg);
    svg.value = out.svg;
    failed.value = false;
  } catch {
    if (mine === seq) { failed.value = true; svg.value = ''; }
  }
}

onMounted(render);
watch(() => [source(), props.isDark], render);
</script>

<template>
  <div class="hq-mermaid">
    <!-- svg is sanitized by mermaid (securityLevel: strict) before injection -->
    <div v-if="svg" class="hq-mermaid__svg" v-html="svg" />
    <div v-else-if="failed" class="hq-mermaid__fail">
      <p class="hq-mermaid__note">{{ t('mermaid.render_failed') }}</p>
      <pre class="hq-mermaid__src"><code>{{ source() }}</code></pre>
    </div>
    <!-- while rendering (first paint, uncached) we render nothing - it's sub-100ms once warm -->
  </div>
</template>

<style scoped>
.hq-mermaid { margin: 0.25rem 0; }
.hq-mermaid__svg { max-width: 100%; overflow-x: auto; }
.hq-mermaid__svg :deep(svg) { max-width: 100%; height: auto; }
.hq-mermaid__note { margin: 0 0 0.4rem; font-size: 0.8125rem; color: var(--ink-muted, var(--hq-muted, currentColor)); }
.hq-mermaid__src {
  margin: 0; padding: 0.6rem 0.75rem; overflow: auto;
  background: var(--surface-sunken, var(--hq-sunken, rgba(128, 128, 128, 0.08)));
  border: 1px solid var(--border-subtle, var(--hq-border, rgba(128, 128, 128, 0.28)));
  border-radius: var(--radius-card, 8px);
}
.hq-mermaid__src code { font-family: var(--font-mono, var(--hq-mono, ui-monospace, monospace)); font-size: 0.8125rem; white-space: pre; }
</style>
